import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Pencil, Layers, Boxes, Loader2, AlertTriangle, Eye, EyeOff, BellRing, BellOff, Sparkles, Settings, Hourglass, CalendarClock } from "lucide-react";
import { InventoryConfigCard, InventoryAIScanDialog, InventoryWaitlistTab } from "../components/InventoryAITools";
import InventoryRentalsTab from "../components/InventoryRentalsTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ITEM_TYPES = ["consumable", "equipment", "mileage", "labor", "other", "rental"];

/**
 * Phase F.4 — Inventory & Job-Cost Templates admin page.
 *
 * Two tabs:
 *   1. Inventory items — list/create/edit/adjust your SKUs.
 *   2. Job templates — saved bundles to "apply to a job" so admin doesn't re-type
 *      the standard consumables / equipment / mileage line items for each visit.
 *
 * When POS LIVE launches it will hit /api/inventory/items/{id}/adjust to keep
 * stock in sync; manual override stays available regardless.
 */
export default function InventoryAdmin() {
  return (
    <div className="space-y-6 p-4">
      <div>
        <h1 className="text-2xl font-bold">Inventory & Job Costing</h1>
        <p className="text-sm text-muted-foreground">
          Track SKUs, save job templates (e.g. "Classic lash set", "Standard residential clean", "2x4 lumber for deck repair"),
          and apply them to jobs to compare planned vs actual cost + time.
        </p>
      </div>
      <Tabs defaultValue="items">
        <TabsList>
          <TabsTrigger value="items" data-testid="inv-tab-items"><Boxes className="h-4 w-4 mr-1" /> Inventory</TabsTrigger>
          <TabsTrigger value="templates" data-testid="inv-tab-templates"><Layers className="h-4 w-4 mr-1" /> Job Templates</TabsTrigger>
          <TabsTrigger value="rentals" data-testid="inv-tab-rentals"><CalendarClock className="h-4 w-4 mr-1" /> Rentals</TabsTrigger>
          <TabsTrigger value="waitlist" data-testid="inv-tab-waitlist"><Hourglass className="h-4 w-4 mr-1" /> Waitlist</TabsTrigger>
          <TabsTrigger value="settings" data-testid="inv-tab-settings"><Settings className="h-4 w-4 mr-1" /> Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="items"><ItemsTab /></TabsContent>
        <TabsContent value="templates"><TemplatesTab /></TabsContent>
        <TabsContent value="rentals"><InventoryRentalsTab /></TabsContent>
        <TabsContent value="waitlist"><InventoryWaitlistTab /></TabsContent>
        <TabsContent value="settings"><InventoryConfigCard /></TabsContent>
      </Tabs>
    </div>
  );
}

function ItemsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [filterLow, setFilterLow] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [aiScanOpen, setAiScanOpen] = useState(false);
  const [aiScanMode, setAiScanMode] = useState("shipment");
  const blank = {
    sku: "", name: "", item_type: "consumable", unit: "ea",
    unit_cost: 0, unit_price: 0, quantity_on_hand: 0,
    reorder_threshold: 0, low_stock_threshold: 0, reorder_qty: 0,
    auto_alert_enabled: true, portal_visible: false,
    is_active: true, notes: "",
    rental_period: "day", rental_deposit: 0, rental_terms: "",
  };
  const load = useCallback(async () => {
    try {
      const [it, al] = await Promise.all([
        axios.get(`${API}/inventory/items`),
        axios.get(`${API}/inventory/alerts`).catch(() => ({ data: { items: [] } })),
      ]);
      setItems(it.data?.items || []);
      setAlerts(al.data?.items || []);
    } catch (e) { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editing.id) await axios.put(`${API}/inventory/items/${editing.id}`, editing);
      else await axios.post(`${API}/inventory/items`, editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const adjust = async (id, delta) => {
    try { await axios.post(`${API}/inventory/items/${id}/adjust`, { delta, reason: "Manual adjust" }); load(); }
    catch (e) { toast.error("Adjust failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Soft-delete this item? Historical job lines will keep referencing its snapshot.")) return;
    try { await axios.delete(`${API}/inventory/items/${id}`); load(); }
    catch (e) { toast.error("Delete failed"); }
  };
  const togglePortal = async (it) => {
    try {
      await axios.put(`${API}/inventory/items/${it.id}/thresholds`, { portal_visible: !it.portal_visible });
      toast.success(it.portal_visible ? "Hidden from portal" : "Visible in portal");
      load();
    } catch (e) { toast.error("Toggle failed"); }
  };
  const toggleAlert = async (it) => {
    try {
      await axios.put(`${API}/inventory/items/${it.id}/thresholds`, { auto_alert_enabled: !it.auto_alert_enabled });
      load();
    } catch (e) { toast.error("Toggle failed"); }
  };
  const scanAlerts = async () => {
    setScanning(true);
    try {
      const r = await axios.post(`${API}/inventory/alerts/scan`);
      toast.success(`Scan complete — ${r.data?.fired || 0} new alert(s) fired`);
      load();
    } catch (e) { toast.error("Scan failed"); }
    finally { setScanning(false); }
  };

  const visibleItems = filterLow ? items.filter((i) => i.is_low_stock) : items;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div><CardTitle>Inventory Items</CardTitle>
            <CardDescription>SKUs you stock or bill for — consumables, equipment, mileage, labor, or "other".</CardDescription>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant={filterLow ? "default" : "outline"} size="sm" onClick={() => setFilterLow(!filterLow)} data-testid="inv-filter-low">
              <AlertTriangle className="h-4 w-4 mr-1" /> {filterLow ? "Show all" : "Below threshold"}
            </Button>
            <Button variant="outline" size="sm" onClick={scanAlerts} disabled={scanning} data-testid="inv-scan-alerts">
              {scanning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <BellRing className="h-4 w-4 mr-1" />} Scan now
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setAiScanMode("shipment"); setAiScanOpen(true); }} data-testid="inv-ai-shipment-btn" className="border-cyan-500/50 text-cyan-600 hover:bg-cyan-50">
              <Sparkles className="h-4 w-4 mr-1" /> AI Scan Shipment
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setAiScanMode("match"); setAiScanOpen(true); }} data-testid="inv-ai-match-btn" className="border-cyan-500/50 text-cyan-600 hover:bg-cyan-50">
              <Sparkles className="h-4 w-4 mr-1" /> AI Match SKU
            </Button>
            <Button onClick={() => setEditing({ ...blank })} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-item-btn"><Plus className="h-4 w-4 mr-1" /> New Item</Button>
          </div>
        </div>
      </CardHeader>
      <InventoryAIScanDialog
        open={aiScanOpen}
        onOpenChange={setAiScanOpen}
        mode={aiScanMode}
        onItemsApplied={() => load()}
      />
      <CardContent>
        {alerts.length > 0 && (
          <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/40 p-3" data-testid="inv-low-stock-banner">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" /> {alerts.length} item{alerts.length === 1 ? "" : "s"} at or below low-stock threshold
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
              {alerts.slice(0, 5).map((a) => `${a.name} (${a.quantity_on_hand} ${a.unit})`).join(" · ")}
              {alerts.length > 5 ? ` …+${alerts.length - 5} more` : ""}
            </p>
          </div>
        )}
        {visibleItems.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="inv-items-empty">{filterLow ? "No items below threshold." : "No items yet — click "}{!filterLow && <strong>New Item</strong>}{!filterLow && " to start."}</p> :
        <div className="space-y-2" data-testid="inv-items-list">
          {visibleItems.map((it) => (
            <div key={it.id} className={`p-3 border rounded-lg flex items-center justify-between gap-2 flex-wrap ${it.is_low_stock ? "border-amber-400 bg-amber-50/40 dark:bg-amber-950/20" : ""}`} data-testid={`inv-item-${it.id}`}>
              <div className="min-w-0">
                <div className="font-medium flex items-center gap-2 flex-wrap">
                  {it.name}
                  <Badge variant="outline">{it.item_type}</Badge>
                  <span className="text-xs text-muted-foreground">{it.sku ? `SKU ${it.sku} · ` : ""}{it.quantity_on_hand} {it.unit}</span>
                  {it.is_low_stock && <Badge variant="destructive" data-testid={`inv-low-${it.id}`}>LOW</Badge>}
                  {it.portal_visible && <Badge variant="secondary"><Eye className="h-3 w-3 mr-0.5" /> portal</Badge>}
                  {!it.is_active && <Badge variant="secondary">inactive</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">
                  Cost ${it.unit_cost.toFixed(2)} · Price ${it.unit_price.toFixed(2)}
                  {it.low_stock_threshold > 0 ? ` · Alert ≤ ${it.low_stock_threshold}` : ""}
                  {it.reorder_qty > 0 ? ` · Reorder qty ${it.reorder_qty}` : ""}
                </p>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => togglePortal(it)} title={it.portal_visible ? "Hide from portal" : "Show in portal"} data-testid={`inv-portal-${it.id}`}>
                  {it.portal_visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                </Button>
                <Button size="sm" variant="outline" onClick={() => toggleAlert(it)} title={it.auto_alert_enabled ? "Disable auto-alert" : "Enable auto-alert"} data-testid={`inv-alert-toggle-${it.id}`}>
                  {it.auto_alert_enabled ? <BellRing className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
                </Button>
                <Button size="sm" variant="outline" onClick={() => adjust(it.id, -1)} data-testid={`inv-adjust-down-${it.id}`}>−1</Button>
                <Button size="sm" variant="outline" onClick={() => adjust(it.id, 1)} data-testid={`inv-adjust-up-${it.id}`}>+1</Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(it)} data-testid={`inv-edit-${it.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(it.id)} data-testid={`inv-del-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>}
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="inv-item-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Item" : "New Item"}</DialogTitle>
            <DialogDescription>Track stock, cost, and default billable price.</DialogDescription></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="inv-item-name" /></div>
                <div><Label>SKU (optional)</Label><Input value={editing.sku || ""} onChange={(e) => setEditing({ ...editing, sku: e.target.value })} data-testid="inv-item-sku" /></div>
                <div><Label>Type</Label>
                  <Select value={editing.item_type} onValueChange={(v) => setEditing({ ...editing, item_type: v })}>
                    <SelectTrigger data-testid="inv-item-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{ITEM_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Unit</Label><Input value={editing.unit} onChange={(e) => setEditing({ ...editing, unit: e.target.value })} placeholder="ea, hr, km, sqft, lb…" data-testid="inv-item-unit" /></div>
                <div><Label>Unit cost ($)</Label><Input type="number" step="0.01" value={editing.unit_cost} onChange={(e) => setEditing({ ...editing, unit_cost: parseFloat(e.target.value) || 0 })} data-testid="inv-item-cost" /></div>
                <div><Label>Default price ($)</Label><Input type="number" step="0.01" value={editing.unit_price} onChange={(e) => setEditing({ ...editing, unit_price: parseFloat(e.target.value) || 0 })} data-testid="inv-item-price" /></div>
                <div><Label>On hand</Label><Input type="number" value={editing.quantity_on_hand} onChange={(e) => setEditing({ ...editing, quantity_on_hand: parseFloat(e.target.value) || 0 })} data-testid="inv-item-qty" /></div>
                <div><Label>Reorder at</Label><Input type="number" value={editing.reorder_threshold} onChange={(e) => setEditing({ ...editing, reorder_threshold: parseFloat(e.target.value) || 0 })} data-testid="inv-item-reorder" /></div>
                <div><Label>Low-stock alert ≤</Label><Input type="number" value={editing.low_stock_threshold || 0} onChange={(e) => setEditing({ ...editing, low_stock_threshold: parseFloat(e.target.value) || 0 })} data-testid="inv-item-low-threshold" /></div>
                <div><Label>Reorder qty (suggested)</Label><Input type="number" value={editing.reorder_qty || 0} onChange={(e) => setEditing({ ...editing, reorder_qty: parseFloat(e.target.value) || 0 })} data-testid="inv-item-reorder-qty" /></div>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <label className="text-sm flex items-center gap-2" data-testid="inv-item-auto-alert-wrap">
                  <input type="checkbox" checked={!!editing.auto_alert_enabled} onChange={(e) => setEditing({ ...editing, auto_alert_enabled: e.target.checked })} data-testid="inv-item-auto-alert" />
                  Auto-generate low-stock alerts
                </label>
                <label className="text-sm flex items-center gap-2" data-testid="inv-item-portal-visible-wrap">
                  <input type="checkbox" checked={!!editing.portal_visible} onChange={(e) => setEditing({ ...editing, portal_visible: e.target.checked })} data-testid="inv-item-portal-visible" />
                  Visible in employee/client portal
                </label>
              </div>
              <div><Label>Notes</Label><Textarea rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} data-testid="inv-item-notes" /></div>
              {/* Phase F.6 Batch C — rental-only fields */}
              {editing.item_type === "rental" && (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 space-y-2" data-testid="inv-rental-block">
                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">Rental settings</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Billing period</Label>
                      <Select value={editing.rental_period || "day"} onValueChange={(v) => setEditing({ ...editing, rental_period: v })}>
                        <SelectTrigger data-testid="inv-rental-period"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hour">per hour</SelectItem>
                          <SelectItem value="day">per day</SelectItem>
                          <SelectItem value="week">per week</SelectItem>
                          <SelectItem value="month">per month</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Refundable deposit / unit</Label>
                      <Input type="number" step="0.01" value={editing.rental_deposit ?? 0}
                        onChange={(e) => setEditing({ ...editing, rental_deposit: parseFloat(e.target.value) || 0 })}
                        data-testid="inv-rental-deposit" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Damage / return policy</Label>
                    <Textarea rows={2} value={editing.rental_terms || ""}
                      onChange={(e) => setEditing({ ...editing, rental_terms: e.target.value })}
                      placeholder="e.g. Late return $25/day. Lost item billed at full replacement cost."
                      data-testid="inv-rental-terms" />
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="inv-item-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    try {
      const [t, i] = await Promise.all([axios.get(`${API}/inventory/templates`), axios.get(`${API}/inventory/items`)]);
      setTemplates(t.data?.templates || []); setItems(i.data?.items || []);
    } catch (e) { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const blank = { name: "", description: "", lines: [] };
  const save = async () => {
    try {
      const body = { name: editing.name, description: editing.description, lines: editing.lines.filter((l) => l.item_id) };
      if (editing.id) await axios.put(`${API}/inventory/templates/${editing.id}`, body);
      else await axios.post(`${API}/inventory/templates`, body);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try { await axios.delete(`${API}/inventory/templates/${id}`); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div><CardTitle>Job Templates</CardTitle>
            <CardDescription>Save the standard line items for a job type — apply later to a scheduled job with one click.</CardDescription>
          </div>
          <Button onClick={() => setEditing({ ...blank })} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-template-btn"><Plus className="h-4 w-4 mr-1" /> New Template</Button>
        </div>
      </CardHeader>
      <CardContent>
        {templates.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="inv-templates-empty">No templates yet. Examples: <em>"Classic lash set", "Volume lash set", "Standard residential clean", "Deck repair — 6ft", "Mileage 20km"</em>.</p> :
        <div className="space-y-2" data-testid="inv-templates-list">
          {templates.map((t) => (
            <div key={t.id} className="p-3 border rounded-lg flex items-center justify-between gap-2 flex-wrap" data-testid={`inv-tpl-${t.id}`}>
              <div className="min-w-0">
                <p className="font-medium">{t.name}</p>
                <p className="text-xs text-muted-foreground">{t.description || "(no description)"} · {t.lines.length} line{t.lines.length === 1 ? "" : "s"}</p>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => setEditing({ ...t })} data-testid={`inv-tpl-edit-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(t.id)} data-testid={`inv-tpl-del-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>}
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="inv-tpl-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Template" : "New Template"}</DialogTitle>
            <DialogDescription>Bundle of line items to apply to a job. Use the per-line "billable" toggle to mark wear-and-tear items as internal-cost-only.</DialogDescription></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Template name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="inv-tpl-name" /></div>
              <div><Label>Description</Label><Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="inv-tpl-desc" /></div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Lines</Label>
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...editing, lines: [...(editing.lines || []), { item_id: "", quantity: 1, is_billable: true, notes: "" }] })} data-testid="inv-tpl-add-line"><Plus className="h-3.5 w-3.5" /> Add line</Button>
                </div>
                {(editing.lines || []).length === 0 ? <p className="text-xs text-muted-foreground mt-1">No lines yet — click <strong>Add line</strong>.</p> :
                <div className="space-y-1 mt-1">
                  {editing.lines.map((line, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-1 items-center" data-testid={`inv-tpl-line-${idx}`}>
                      <div className="col-span-5">
                        <Select value={line.item_id || ""} onValueChange={(v) => {
                          const lines = [...editing.lines]; lines[idx] = { ...line, item_id: v }; setEditing({ ...editing, lines });
                        }}>
                          <SelectTrigger className="h-8" data-testid={`inv-tpl-line-item-${idx}`}><SelectValue placeholder="Pick item…" /></SelectTrigger>
                          <SelectContent>{items.filter((i) => i.is_active).map((i) => <SelectItem key={i.id} value={i.id}>{i.name} ({i.unit})</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2"><Input type="number" step="0.01" value={line.quantity} onChange={(e) => { const lines = [...editing.lines]; lines[idx] = { ...line, quantity: parseFloat(e.target.value) || 0 }; setEditing({ ...editing, lines }); }} data-testid={`inv-tpl-line-qty-${idx}`} /></div>
                      <label className="col-span-2 text-xs flex items-center gap-1">
                        <input type="checkbox" checked={!!line.is_billable} onChange={(e) => { const lines = [...editing.lines]; lines[idx] = { ...line, is_billable: e.target.checked }; setEditing({ ...editing, lines }); }} data-testid={`inv-tpl-line-bill-${idx}`} />
                        billable
                      </label>
                      <div className="col-span-2"><Input placeholder="note" value={line.notes || ""} onChange={(e) => { const lines = [...editing.lines]; lines[idx] = { ...line, notes: e.target.value }; setEditing({ ...editing, lines }); }} data-testid={`inv-tpl-line-note-${idx}`} /></div>
                      <Button size="sm" variant="ghost" className="col-span-1 text-rose-600" onClick={() => setEditing({ ...editing, lines: editing.lines.filter((_, i) => i !== idx) })} data-testid={`inv-tpl-line-del-${idx}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  ))}
                </div>}
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="inv-tpl-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
